import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FlagIcon } from '../components/LanguageSelector';

const content = {
  no: {
    title: 'Personvernerklæring',
    effectiveDate: 'Gjeldende fra 1. mars 2026',
    lastUpdated: 'Sist oppdatert: 19. mars 2026',
    backLabel: '\u2190 Tilbake til Tempo',
    backLink: '/',
    switchLabel: 'English',
    switchCode: 'en',
    switchLink: '/en/privacy',
    toc: 'Innhold',
    sections: [
      {
        id: 'intro',
        heading: '1. Introduksjon',
        content: [
          { type: 'p', text: 'Denne personvernerklæringen beskriver hvordan Kviteberg AS (org.nr. 834 606 402), gjennom produktet Tempo («Tempo», «vi», «oss»), samler inn, behandler, lagrer og beskytter personopplysningene dine når du bruker Tempo-appen, nettsiden tempo.app og tilknyttede tjenester (samlet kalt «Tjenesten»).' },
          { type: 'p', text: 'Kviteberg AS er behandlingsansvarlig for personopplysningene som beskrives i denne erklæringen. Vi er forpliktet til å beskytte personvernet ditt i samsvar med EUs personvernforordning (GDPR), den norske personopplysningsloven og gjeldende lovgivning.' },
          { type: 'p', text: 'Ved å bruke Tjenesten samtykker du til behandlingen som beskrives i denne erklæringen. Hvis du ikke godtar vilkårene, ber vi deg om ikke å bruke Tjenesten.' },
        ],
      },
      {
        id: 'controller',
        heading: '2. Behandlingsansvarlig',
        content: [
          { type: 'table', rows: [
            ['Selskap', 'Kviteberg AS (driver Tempo)'],
            ['Org.nr.', '834 606 402'],
            ['Adresse', 'Oslo, Norge'],
            ['E-post', 'privacy@tempo.app'],
            ['Personvernombud', 'dpo@tempo.app'],
          ]},
          { type: 'p', text: 'For spørsmål om personvern, kontakt oss på privacy@tempo.app eller vårt personvernombud på dpo@tempo.app.' },
        ],
      },
      {
        id: 'data-collected',
        heading: '3. Personopplysninger vi samler inn',
        content: [
          { type: 'p', text: 'Vi samler inn og behandler følgende kategorier av personopplysninger:' },
          { type: 'subheading', text: '3.1 Kontoinformasjon' },
          { type: 'list', items: [
            'Fullt navn',
            'E-postadresse',
            'Telefonnummer',
            'Passord (lagres kun som kryptert hash \u2014 aldri i klartekst)',
            'Kontoopprettelsesdato',
          ]},
          { type: 'subheading', text: '3.2 Helse- og treningsdata' },
          { type: 'p', text: 'Helse- og treningsdata behandles som \u00absærlige kategorier av personopplysninger\u00bb i henhold til GDPR artikkel 9, og krever ditt uttrykkelige samtykke.' },
          { type: 'list', items: [
            'Løpehistorikk (distanse, varighet, tempo, rute)',
            'Pulssoner og gjennomsnittlig puls',
            'Treningsbelastning og restitusjonsstatus',
            'Aktivitetstype (løp, intervall, hvile)',
            'Data importert fra tredjepartstjenester (f.eks. Garmin Connect, Apple Health)',
          ]},
          { type: 'subheading', text: '3.3 Profil- og preferansedata' },
          { type: 'list', items: [
            'Treningsmål (5K, 10K, halvmaraton, maraton)',
            'Erfaringsnivå (begynner, mellomnivå, avansert)',
            'Måldato og eventuelle løpsarrangementer',
            'Kostholdspreferanser og allergier',
            'Foretrukket coaching-tone (støttende, balansert, direkte)',
          ]},
          { type: 'subheading', text: '3.4 Bruksdata' },
          { type: 'list', items: [
            'Appinteraksjoner og sidevisninger',
            'Samtalehistorikk med AI-coach',
            'Tidspunkt for appbruk',
            'Enhetstype, operativsystem og appversjon',
            'IP-adresse (anonymiseres etter 30 dager)',
          ]},
          { type: 'subheading', text: '3.5 Data vi IKKE samler inn' },
          { type: 'list', items: [
            'Presis GPS-posisjon eller rutedata (med mindre du eksplisitt deler det)',
            'Biometriske data for identifisering',
            'Finansiell informasjon (betalinger håndteres av tredjepart)',
            'Data fra kontaktlisten din eller sosiale medier',
          ]},
        ],
      },
      {
        id: 'legal-basis',
        heading: '4. Rettslig grunnlag for behandling',
        content: [
          { type: 'p', text: 'Vi behandler personopplysningene dine basert på følgende rettslige grunnlag i henhold til GDPR artikkel 6 og 9:' },
          { type: 'table', rows: [
            ['Formål', 'Rettslig grunnlag', 'GDPR-artikkel'],
            ['Kontoadministrasjon', 'Oppfyllelse av avtale', 'Art. 6(1)(b)'],
            ['Personlig treningsplan', 'Oppfyllelse av avtale', 'Art. 6(1)(b)'],
            ['Helse-/treningsdata', 'Uttrykkelig samtykke', 'Art. 9(2)(a)'],
            ['AI-basert coaching', 'Uttrykkelig samtykke', 'Art. 9(2)(a)'],
            ['Tjenestforbedring', 'Berettiget interesse', 'Art. 6(1)(f)'],
            ['Sikkerhet og svindel', 'Berettiget interesse', 'Art. 6(1)(f)'],
            ['Lovpålagte krav', 'Rettslig forpliktelse', 'Art. 6(1)(c)'],
            ['Markedsføring (e-post)', 'Samtykke', 'Art. 6(1)(a)'],
          ]},
        ],
      },
      {
        id: 'how-we-use',
        heading: '5. Hvordan vi bruker personopplysningene',
        content: [
          { type: 'subheading', text: '5.1 Kjernetjeneste' },
          { type: 'list', items: [
            'Generere og vedlikeholde din personlige treningsplan',
            'Levere daglige trenings- og ernæringsanbefalinger',
            'Tilpasse AI-coachens råd basert på din progresjon og respons',
            'Synkronisere data med tilkoblede tredjepartstjenester',
          ]},
          { type: 'subheading', text: '5.2 Kommunikasjon' },
          { type: 'list', items: [
            'Sende daglige coaching-meldinger og treningspåminnelser',
            'Varsle om endringer i planen din',
            'Sende driftsmeldinger og sikkerhetsoppdateringer',
          ]},
          { type: 'subheading', text: '5.3 Forbedring og analyse' },
          { type: 'list', items: [
            'Analysere anonymiserte bruksmønstre for å forbedre tjenesten',
            'Utvikle og forbedre AI-modeller for coaching (kun aggregerte, anonymiserte data)',
            'Overvåke og vedlikeholde tjenestens ytelse og sikkerhet',
          ]},
        ],
      },
      {
        id: 'ai',
        heading: '6. AI-behandling og automatiserte beslutninger',
        content: [
          { type: 'p', text: 'Tempo bruker kunstig intelligens (AI) for å levere personlig tilpasset coaching. Dette er en sentral del av tjenesten og innebærer:' },
          { type: 'subheading', text: '6.1 Hva AI brukes til' },
          { type: 'list', items: [
            'Analyse av treningsdata for å generere personlige treningsplaner',
            'Daglige anbefalinger for trening, hvile og ernæring',
            'Samtalebasert coaching via chat-funksjonen',
            'Automatisk tilpasning av treningsplanen basert på respons og progresjon',
          ]},
          { type: 'subheading', text: '6.2 AI-leverandør' },
          { type: 'p', text: 'AI-tjenesten leveres av OpenAI (USA). Data som sendes til AI-modellen inkluderer din treningshistorikk, profil og samtalehistorikk. Data behandles i henhold til vår databehandleravtale med OpenAI og overføres i samsvar med GDPR kapittel V (se avsnitt 8).' },
          { type: 'subheading', text: '6.3 Dine rettigheter ved automatiserte beslutninger' },
          { type: 'p', text: 'I henhold til GDPR artikkel 22 har du rett til å:' },
          { type: 'list', items: [
            'Få informasjon om logikken bak automatiserte beslutninger',
            'Bestride automatiserte beslutninger og be om menneskelig gjennomgang',
            'Uttrykke ditt synspunkt angående automatiserte beslutninger',
          ]},
          { type: 'p', text: 'Kontakt oss på privacy@tempo.app for å utøve disse rettighetene.' },
        ],
      },
      {
        id: 'sharing',
        heading: '7. Deling med tredjeparter',
        content: [
          { type: 'p', text: 'Vi selger aldri personopplysningene dine. Vi deler data kun i følgende tilfeller:' },
          { type: 'subheading', text: '7.1 Tjenesteleverandører (databehandlere)' },
          { type: 'table', rows: [
            ['Tjenesteleverandør', 'Formål', 'Data som deles', 'Lokasjon'],
            ['MongoDB Atlas', 'Databasehosting', 'Alle brukerdata (kryptert)', 'EU (Irland)'],
            ['OpenAI', 'AI-coaching', 'Trenings-/profildata, samtaler', 'USA*'],
            ['Garmin Connect**', 'Aktivitetssynkronisering', 'Treningsdata', 'USA*'],
            ['Apple HealthKit**', 'Aktivitetssynkronisering', 'Treningsdata', 'Lokalt på enhet'],
          ]},
          { type: 'p', text: '* Overføring til USA skjer i henhold til EU-U.S. Data Privacy Framework eller Standard Contractual Clauses (SCC).' },
          { type: 'p', text: '** Kun når du aktivt kobler til disse tjenestene.' },
          { type: 'subheading', text: '7.2 Integrasjonspartnere' },
          { type: 'p', text: 'Når du kobler Tempo til tredjepartstjenester som Garmin Connect:' },
          { type: 'list', items: [
            'Vi henter kun data du eksplisitt godkjenner (aktiviteter, puls, osv.)',
            'Du kan når som helst koble fra integrasjonen i appens innstillinger',
            'Vi lagrer synkronisert data i henhold til denne erklæringen',
            'Tredjepartens personvernerklæring gjelder for data de behandler',
          ]},
          { type: 'subheading', text: '7.3 Lovpålagt utlevering' },
          { type: 'p', text: 'Vi kan utlevere personopplysninger hvis vi er rettslig forpliktet til det, f.eks. ved pålegg fra domstol eller tilsynsmyndighet.' },
        ],
      },
      {
        id: 'transfers',
        heading: '8. Overføring utenfor EØS',
        content: [
          { type: 'p', text: 'Noen av våre tjenesteleverandører befinner seg utenfor EØS. For å sikre tilstrekkelig beskyttelse ved slike overføringer bruker vi:' },
          { type: 'list', items: [
            'EU-U.S. Data Privacy Framework (for amerikanske leverandører som er sertifisert)',
            'Standard Contractual Clauses (SCC) godkjent av EU-kommisjonen',
            'Supplerende sikkerhetstiltak som kryptering og pseudonymisering',
          ]},
          { type: 'p', text: 'Du kan be om en kopi av overføringsmekanismene ved å kontakte privacy@tempo.app.' },
        ],
      },
      {
        id: 'retention',
        heading: '9. Oppbevaringsperioder',
        content: [
          { type: 'table', rows: [
            ['Datatype', 'Oppbevaringsperiode', 'Begrunnelse'],
            ['Kontoinformasjon', 'Så lenge kontoen er aktiv + 30 dager', 'Nødvendig for tjenesten'],
            ['Treningsdata', 'Så lenge kontoen er aktiv + 30 dager', 'Nødvendig for tjenesten'],
            ['Samtalehistorikk', '12 måneder etter siste melding', 'Kontekstuell coaching'],
            ['Bruksdata', '24 måneder (anonymisert etter 6 mnd)', 'Tjenesteforbedring'],
            ['IP-adresser', '30 dager', 'Sikkerhet'],
            ['Slettede kontoer', 'All data slettes innen 30 dager', 'GDPR-krav'],
          ]},
          { type: 'p', text: 'Når du sletter kontoen din, slettes alle personopplysninger permanent innen 30 dager. Anonymiserte, aggregerte data som ikke kan knyttes tilbake til deg, kan beholdes for statistiske formål.' },
        ],
      },
      {
        id: 'rights',
        heading: '10. Dine rettigheter',
        content: [
          { type: 'p', text: 'I henhold til GDPR har du følgende rettigheter:' },
          { type: 'table', rows: [
            ['Rettighet', 'Beskrivelse', 'GDPR-artikkel'],
            ['Innsyn', 'Be om kopi av alle personopplysninger vi har om deg', 'Art. 15'],
            ['Retting', 'Korrigere unøyaktige personopplysninger', 'Art. 16'],
            ['Sletting', 'Be om at vi sletter alle dine data (\u00abrett til å bli glemt\u00bb)', 'Art. 17'],
            ['Begrensning', 'Begrense behandlingen av dine data', 'Art. 18'],
            ['Dataportabilitet', 'Motta dine data i et strukturert, maskinlesbart format', 'Art. 20'],
            ['Innsigelse', 'Protestere mot behandling basert på berettiget interesse', 'Art. 21'],
            ['Tilbakekalling', 'Trekke tilbake samtykke når som helst', 'Art. 7(3)'],
          ]},
          { type: 'subheading', text: 'Slik utøver du rettighetene dine' },
          { type: 'list', items: [
            'I appen: Gå til Profil \u2192 Personvern for å laste ned eller slette data',
            'Via e-post: Send forespørsel til privacy@tempo.app',
            'Vi svarer innen 30 dager i henhold til GDPR',
            'Du har rett til å klage til Datatilsynet (datatilsynet.no)',
          ]},
        ],
      },
      {
        id: 'security',
        heading: '11. Sikkerhet',
        content: [
          { type: 'p', text: 'Vi bruker bransjestandard sikkerhetstiltak for å beskytte dine data:' },
          { type: 'list', items: [
            'TLS 1.3-kryptering for all dataoverføring',
            'AES-256-kryptering for data i hvile',
            'Passord hashet med bcrypt (aldri lagret i klartekst)',
            'JWT-basert autentisering med begrenset levetid',
            'Rollebasert tilgangskontroll for administratorer',
            'Regelmessige sikkerhetsgjennomganger og penetrasjonstester',
            'Overvåking og logging av alle systemtilganger',
            'Hendelseshåndteringsplan i henhold til GDPR artikkel 33 og 34',
          ]},
          { type: 'p', text: 'Ved et sikkerhetsbrudd varsler vi berørte brukere og Datatilsynet innen 72 timer, i henhold til GDPR.' },
        ],
      },
      {
        id: 'children',
        heading: '12. Barn',
        content: [
          { type: 'p', text: 'Tempo er ikke rettet mot barn under 16 år. Vi samler ikke bevisst inn personopplysninger fra barn under 16 år. Hvis vi oppdager at vi har mottatt data fra et barn, vil vi slette det umiddelbart.' },
          { type: 'p', text: 'Hvis du er forelder eller foresatt og tror at barnet ditt har opprettet en konto, kontakt oss på privacy@tempo.app.' },
        ],
      },
      {
        id: 'cookies',
        heading: '13. Informasjonskapsler og sporing',
        content: [
          { type: 'p', text: 'Nettsiden vår (tempo.app) bruker minimale informasjonskapsler:' },
          { type: 'table', rows: [
            ['Type', 'Formål', 'Varighet', 'Påkrevd'],
            ['Sesjonskapsler', 'Innlogging og autentisering', 'Sesjon', 'Ja'],
            ['Preferansekapsler', 'Språkvalg', '1 år', 'Ja'],
            ['Analysekapsler', 'Anonym bruksstatistikk', '24 mnd', 'Nei (samtykke)'],
          ]},
          { type: 'p', text: 'Vi bruker IKKE:' },
          { type: 'list', items: [
            'Tredjepartssporingskapsler for reklame',
            'Fingerprinting eller skjult sporing',
            'Kryssplattform sporingsteknologi',
          ]},
        ],
      },
      {
        id: 'changes',
        heading: '14. Endringer i denne erklæringen',
        content: [
          { type: 'p', text: 'Vi kan oppdatere denne personvernerklæringen fra tid til annen. Ved vesentlige endringer vil vi:' },
          { type: 'list', items: [
            'Varsle deg via e-post og/eller push-varsel',
            'Publisere den oppdaterte erklæringen med ny ikrafttredelsesdato',
            'Be om nytt samtykke hvis endringen krever det i henhold til GDPR',
          ]},
          { type: 'p', text: 'Vi anbefaler å gjennomgå denne erklæringen regelmessig.' },
        ],
      },
      {
        id: 'contact',
        heading: '15. Kontakt oss',
        content: [
          { type: 'table', rows: [
            ['Generelle henvendelser', 'hello@tempo.app'],
            ['Personvern', 'privacy@tempo.app'],
            ['Personvernombud (DPO)', 'dpo@tempo.app'],
            ['Postadresse', 'Kviteberg AS, Oslo, Norge'],
          ]},
          { type: 'p', text: 'Du har rett til å klage til Datatilsynet hvis du mener at vi behandler personopplysningene dine i strid med GDPR:' },
          { type: 'table', rows: [
            ['Datatilsynet', 'www.datatilsynet.no'],
            ['E-post', 'postkasse@datatilsynet.no'],
            ['Telefon', '+47 22 39 69 00'],
          ]},
        ],
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    effectiveDate: 'Effective from March 1, 2026',
    lastUpdated: 'Last updated: March 19, 2026',
    backLabel: '\u2190 Back to Tempo',
    backLink: '/en',
    switchLabel: 'Norsk',
    switchCode: 'no',
    switchLink: '/personvern',
    toc: 'Contents',
    sections: [
      {
        id: 'intro',
        heading: '1. Introduction',
        content: [
          { type: 'p', text: 'This Privacy Policy describes how Kviteberg AS (org. no. 834 606 402), through its product Tempo (\u201cTempo\u201d, \u201cwe\u201d, \u201cus\u201d), collects, processes, stores and protects your personal data when you use the Tempo app, the website tempo.app and associated services (collectively the \u201cService\u201d).' },
          { type: 'p', text: 'Kviteberg AS is the data controller for the personal data described in this policy. We are committed to protecting your privacy in accordance with the EU General Data Protection Regulation (GDPR), the Norwegian Personal Data Act and applicable law.' },
          { type: 'p', text: 'By using the Service, you consent to the processing described in this policy. If you do not agree to these terms, please do not use the Service.' },
        ],
      },
      {
        id: 'controller',
        heading: '2. Data Controller',
        content: [
          { type: 'table', rows: [
            ['Company', 'Kviteberg AS (operating Tempo)'],
            ['Reg. no.', '834 606 402'],
            ['Address', 'Oslo, Norway'],
            ['Email', 'privacy@tempo.app'],
            ['Data Protection Officer', 'dpo@tempo.app'],
          ]},
          { type: 'p', text: 'For privacy-related inquiries, contact us at privacy@tempo.app or our Data Protection Officer at dpo@tempo.app.' },
        ],
      },
      {
        id: 'data-collected',
        heading: '3. Personal Data We Collect',
        content: [
          { type: 'p', text: 'We collect and process the following categories of personal data:' },
          { type: 'subheading', text: '3.1 Account Information' },
          { type: 'list', items: [
            'Full name',
            'Email address',
            'Phone number',
            'Password (stored only as an encrypted hash \u2014 never in plain text)',
            'Account creation date',
          ]},
          { type: 'subheading', text: '3.2 Health & Training Data' },
          { type: 'p', text: 'Health and training data is treated as \u201cspecial categories of personal data\u201d under GDPR Article 9 and requires your explicit consent.' },
          { type: 'list', items: [
            'Running history (distance, duration, pace, route)',
            'Heart rate zones and average heart rate',
            'Training load and recovery status',
            'Activity type (run, interval, rest)',
            'Data imported from third-party services (e.g. Garmin Connect, Apple Health)',
          ]},
          { type: 'subheading', text: '3.3 Profile & Preference Data' },
          { type: 'list', items: [
            'Training goal (5K, 10K, half marathon, marathon)',
            'Experience level (beginner, intermediate, advanced)',
            'Target date and any race events',
            'Dietary preferences and allergies',
            'Preferred coaching tone (supportive, balanced, direct)',
          ]},
          { type: 'subheading', text: '3.4 Usage Data' },
          { type: 'list', items: [
            'App interactions and page views',
            'Conversation history with AI coach',
            'Timestamps of app usage',
            'Device type, operating system and app version',
            'IP address (anonymized after 30 days)',
          ]},
          { type: 'subheading', text: '3.5 Data We Do NOT Collect' },
          { type: 'list', items: [
            'Precise GPS location or route data (unless you explicitly share it)',
            'Biometric data for identification',
            'Financial information (payments are handled by third parties)',
            'Data from your contacts or social media',
          ]},
        ],
      },
      {
        id: 'legal-basis',
        heading: '4. Legal Basis for Processing',
        content: [
          { type: 'p', text: 'We process your personal data based on the following legal grounds under GDPR Articles 6 and 9:' },
          { type: 'table', rows: [
            ['Purpose', 'Legal Basis', 'GDPR Article'],
            ['Account management', 'Performance of contract', 'Art. 6(1)(b)'],
            ['Personal training plan', 'Performance of contract', 'Art. 6(1)(b)'],
            ['Health/training data', 'Explicit consent', 'Art. 9(2)(a)'],
            ['AI-based coaching', 'Explicit consent', 'Art. 9(2)(a)'],
            ['Service improvement', 'Legitimate interest', 'Art. 6(1)(f)'],
            ['Security and fraud', 'Legitimate interest', 'Art. 6(1)(f)'],
            ['Legal obligations', 'Legal obligation', 'Art. 6(1)(c)'],
            ['Marketing (email)', 'Consent', 'Art. 6(1)(a)'],
          ]},
        ],
      },
      {
        id: 'how-we-use',
        heading: '5. How We Use Your Data',
        content: [
          { type: 'subheading', text: '5.1 Core Service' },
          { type: 'list', items: [
            'Generate and maintain your personal training plan',
            'Deliver daily training and nutrition recommendations',
            'Adapt the AI coach\u2019s advice based on your progression and response',
            'Synchronize data with connected third-party services',
          ]},
          { type: 'subheading', text: '5.2 Communication' },
          { type: 'list', items: [
            'Send daily coaching messages and training reminders',
            'Notify you of changes to your plan',
            'Send operational messages and security updates',
          ]},
          { type: 'subheading', text: '5.3 Improvement & Analytics' },
          { type: 'list', items: [
            'Analyze anonymized usage patterns to improve the service',
            'Develop and improve AI models for coaching (aggregated, anonymized data only)',
            'Monitor and maintain service performance and security',
          ]},
        ],
      },
      {
        id: 'ai',
        heading: '6. AI Processing & Automated Decisions',
        content: [
          { type: 'p', text: 'Tempo uses artificial intelligence (AI) to deliver personalized coaching. This is a core part of the service and involves:' },
          { type: 'subheading', text: '6.1 What AI Is Used For' },
          { type: 'list', items: [
            'Analysis of training data to generate personal training plans',
            'Daily recommendations for training, rest and nutrition',
            'Conversational coaching via the chat feature',
            'Automatic plan adjustments based on response and progression',
          ]},
          { type: 'subheading', text: '6.2 AI Provider' },
          { type: 'p', text: 'The AI service is provided by OpenAI (USA). Data sent to the AI model includes your training history, profile and conversation history. Data is processed under our Data Processing Agreement with OpenAI and transferred in accordance with GDPR Chapter V (see Section 8).' },
          { type: 'subheading', text: '6.3 Your Rights Regarding Automated Decisions' },
          { type: 'p', text: 'Under GDPR Article 22, you have the right to:' },
          { type: 'list', items: [
            'Receive information about the logic behind automated decisions',
            'Contest automated decisions and request human review',
            'Express your point of view regarding automated decisions',
          ]},
          { type: 'p', text: 'Contact us at privacy@tempo.app to exercise these rights.' },
        ],
      },
      {
        id: 'sharing',
        heading: '7. Third-Party Sharing',
        content: [
          { type: 'p', text: 'We never sell your personal data. We share data only in the following cases:' },
          { type: 'subheading', text: '7.1 Service Providers (Data Processors)' },
          { type: 'table', rows: [
            ['Service Provider', 'Purpose', 'Data Shared', 'Location'],
            ['MongoDB Atlas', 'Database hosting', 'All user data (encrypted)', 'EU (Ireland)'],
            ['OpenAI', 'AI coaching', 'Training/profile data, conversations', 'USA*'],
            ['Garmin Connect**', 'Activity sync', 'Training data', 'USA*'],
            ['Apple HealthKit**', 'Activity sync', 'Training data', 'On-device'],
          ]},
          { type: 'p', text: '* Transfer to the USA is governed by the EU-U.S. Data Privacy Framework or Standard Contractual Clauses (SCC).' },
          { type: 'p', text: '** Only when you actively connect these services.' },
          { type: 'subheading', text: '7.2 Integration Partners' },
          { type: 'p', text: 'When you connect Tempo to third-party services such as Garmin Connect:' },
          { type: 'list', items: [
            'We only fetch data you explicitly approve (activities, heart rate, etc.)',
            'You can disconnect the integration at any time in app settings',
            'We store synchronized data in accordance with this policy',
            'The third party\u2019s privacy policy applies to data they process',
          ]},
          { type: 'subheading', text: '7.3 Legal Disclosure' },
          { type: 'p', text: 'We may disclose personal data if legally required to do so, e.g. by court order or regulatory authority.' },
        ],
      },
      {
        id: 'transfers',
        heading: '8. International Data Transfers',
        content: [
          { type: 'p', text: 'Some of our service providers are located outside the EEA. To ensure adequate protection for such transfers, we use:' },
          { type: 'list', items: [
            'EU-U.S. Data Privacy Framework (for certified US providers)',
            'Standard Contractual Clauses (SCC) approved by the European Commission',
            'Supplementary security measures such as encryption and pseudonymization',
          ]},
          { type: 'p', text: 'You can request a copy of the transfer mechanisms by contacting privacy@tempo.app.' },
        ],
      },
      {
        id: 'retention',
        heading: '9. Data Retention',
        content: [
          { type: 'table', rows: [
            ['Data Type', 'Retention Period', 'Rationale'],
            ['Account information', 'As long as account is active + 30 days', 'Required for service'],
            ['Training data', 'As long as account is active + 30 days', 'Required for service'],
            ['Conversation history', '12 months after last message', 'Contextual coaching'],
            ['Usage data', '24 months (anonymized after 6 months)', 'Service improvement'],
            ['IP addresses', '30 days', 'Security'],
            ['Deleted accounts', 'All data deleted within 30 days', 'GDPR requirement'],
          ]},
          { type: 'p', text: 'When you delete your account, all personal data is permanently deleted within 30 days. Anonymized, aggregated data that cannot be linked back to you may be retained for statistical purposes.' },
        ],
      },
      {
        id: 'rights',
        heading: '10. Your Rights',
        content: [
          { type: 'p', text: 'Under the GDPR, you have the following rights:' },
          { type: 'table', rows: [
            ['Right', 'Description', 'GDPR Article'],
            ['Access', 'Request a copy of all personal data we hold about you', 'Art. 15'],
            ['Rectification', 'Correct inaccurate personal data', 'Art. 16'],
            ['Erasure', 'Request deletion of all your data (\u201cright to be forgotten\u201d)', 'Art. 17'],
            ['Restriction', 'Restrict the processing of your data', 'Art. 18'],
            ['Data portability', 'Receive your data in a structured, machine-readable format', 'Art. 20'],
            ['Objection', 'Object to processing based on legitimate interest', 'Art. 21'],
            ['Withdrawal', 'Withdraw consent at any time', 'Art. 7(3)'],
          ]},
          { type: 'subheading', text: 'How to Exercise Your Rights' },
          { type: 'list', items: [
            'In the app: Go to Profile \u2192 Privacy to download or delete data',
            'Via email: Send request to privacy@tempo.app',
            'We respond within 30 days in accordance with GDPR',
            'You have the right to lodge a complaint with the Norwegian Data Protection Authority (datatilsynet.no)',
          ]},
        ],
      },
      {
        id: 'security',
        heading: '11. Security',
        content: [
          { type: 'p', text: 'We use industry-standard security measures to protect your data:' },
          { type: 'list', items: [
            'TLS 1.3 encryption for all data in transit',
            'AES-256 encryption for data at rest',
            'Passwords hashed with bcrypt (never stored in plain text)',
            'JWT-based authentication with limited lifetime',
            'Role-based access control for administrators',
            'Regular security audits and penetration testing',
            'Monitoring and logging of all system access',
            'Incident response plan in accordance with GDPR Articles 33 and 34',
          ]},
          { type: 'p', text: 'In the event of a security breach, we will notify affected users and the Norwegian Data Protection Authority within 72 hours, in accordance with the GDPR.' },
        ],
      },
      {
        id: 'children',
        heading: '12. Children',
        content: [
          { type: 'p', text: 'Tempo is not directed at children under 16 years of age. We do not knowingly collect personal data from children under 16. If we discover that we have received data from a child, we will delete it immediately.' },
          { type: 'p', text: 'If you are a parent or guardian and believe your child has created an account, contact us at privacy@tempo.app.' },
        ],
      },
      {
        id: 'cookies',
        heading: '13. Cookies & Tracking',
        content: [
          { type: 'p', text: 'Our website (tempo.app) uses minimal cookies:' },
          { type: 'table', rows: [
            ['Type', 'Purpose', 'Duration', 'Required'],
            ['Session cookies', 'Login and authentication', 'Session', 'Yes'],
            ['Preference cookies', 'Language selection', '1 year', 'Yes'],
            ['Analytics cookies', 'Anonymous usage statistics', '24 months', 'No (consent)'],
          ]},
          { type: 'p', text: 'We do NOT use:' },
          { type: 'list', items: [
            'Third-party tracking cookies for advertising',
            'Fingerprinting or hidden tracking',
            'Cross-platform tracking technology',
          ]},
        ],
      },
      {
        id: 'changes',
        heading: '14. Changes to This Policy',
        content: [
          { type: 'p', text: 'We may update this Privacy Policy from time to time. For material changes, we will:' },
          { type: 'list', items: [
            'Notify you via email and/or push notification',
            'Publish the updated policy with a new effective date',
            'Request renewed consent if required under GDPR',
          ]},
          { type: 'p', text: 'We recommend reviewing this policy regularly.' },
        ],
      },
      {
        id: 'contact',
        heading: '15. Contact Us',
        content: [
          { type: 'table', rows: [
            ['General inquiries', 'hello@tempo.app'],
            ['Privacy', 'privacy@tempo.app'],
            ['Data Protection Officer', 'dpo@tempo.app'],
            ['Postal address', 'Kviteberg AS, Oslo, Norway'],
          ]},
          { type: 'p', text: 'You have the right to lodge a complaint with the Norwegian Data Protection Authority if you believe we are processing your personal data in violation of the GDPR:' },
          { type: 'table', rows: [
            ['Datatilsynet', 'www.datatilsynet.no'],
            ['Email', 'postkasse@datatilsynet.no'],
            ['Phone', '+47 22 39 69 00'],
          ]},
        ],
      },
    ],
  },
};

function RenderContent({ items }) {
  return items.map((item, i) => {
    if (item.type === 'p') {
      return <p key={i} className="text-sm sm:text-[15px] text-white/50 leading-[1.75] mb-4">{item.text}</p>;
    }
    if (item.type === 'subheading') {
      return <h3 key={i} className="text-white/80 font-semibold text-sm sm:text-[15px] mt-6 mb-2">{item.text}</h3>;
    }
    if (item.type === 'list') {
      return (
        <ul key={i} className="space-y-1.5 mb-4 pl-1">
          {item.items.map((li, j) => (
            <li key={j} className="text-sm sm:text-[15px] text-white/50 leading-[1.75] flex items-start gap-2.5">
              <span className="text-[#CCFF00]/40 mt-[9px] text-[6px]">{'\u25CF'}</span>
              <span>{li}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (item.type === 'table') {
      const isHeader = item.rows[0].length > 2;
      return (
        <div key={i} className="mb-4 rounded-[12px] border border-white/[0.06] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              {isHeader && (
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    {item.rows[0].map((cell, j) => (
                      <th key={j} className="text-left px-4 py-2.5 text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-white/30 font-medium whitespace-nowrap">{cell}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {(isHeader ? item.rows.slice(1) : item.rows).map((row, j) => (
                  <tr key={j} className="border-b border-white/[0.03] last:border-0">
                    {row.map((cell, k) => (
                      <td key={k} className={`px-4 py-2.5 text-xs sm:text-sm ${k === 0 ? 'text-white/60 font-medium' : 'text-white/40'} whitespace-nowrap`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    return null;
  });
}

export default function PrivacyPage() {
  const location = useLocation();
  const lang = location.pathname.startsWith('/en') ? 'en' : 'no';
  const t = content[lang];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto max-w-[900px] px-5 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link to={t.backLink} className="text-sm text-white/40 hover:text-white/70 transition-colors duration-180">
            {t.backLabel}
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block font-['Barlow_Condensed'] font-bold text-sm tracking-wider text-white/20">TEMPO</span>
            <Link
              to={t.switchLink}
              className="flex items-center gap-2 h-8 px-2.5 rounded-[10px] border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-[background-color] duration-180 text-xs text-white/50"
            >
              <FlagIcon code={t.switchCode} size={16} />
              <span>{t.switchLabel}</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[900px] px-5 sm:px-6">
        <div className="grid lg:grid-cols-[220px_1fr] gap-8 lg:gap-12">
          {/* Sidebar TOC - desktop */}
          <aside className="hidden lg:block pt-12 sticky top-16 self-start max-h-[calc(100vh-80px)] overflow-y-auto">
            <p className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/25 mb-4">{t.toc}</p>
            <nav className="space-y-0.5">
              {t.sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block py-1.5 text-[12px] text-white/30 hover:text-white/60 transition-colors duration-180 leading-snug"
                >
                  {s.heading}
                </a>
              ))}
            </nav>
          </aside>

          {/* Main content */}
          <main className="py-10 sm:py-14 lg:py-12 min-w-0">
            {/* Title */}
            <div className="mb-10 sm:mb-14 pb-8 border-b border-white/5">
              <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#CCFF00]/40 mb-3">TEMPO</p>
              <h1 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-4">{t.title}</h1>
              <div className="flex flex-wrap gap-4 text-xs text-white/25">
                <span>{t.effectiveDate}</span>
                <span className="text-white/10">|</span>
                <span>{t.lastUpdated}</span>
              </div>
            </div>

            {/* Mobile TOC */}
            <div className="lg:hidden mb-10 rounded-[14px] bg-white/[0.02] border border-white/5 p-5">
              <p className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/25 mb-3">{t.toc}</p>
              <nav className="space-y-1 columns-1 sm:columns-2 gap-6">
                {t.sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block py-1 text-xs text-white/35 hover:text-white/60 transition-colors duration-180 break-inside-avoid"
                  >
                    {s.heading}
                  </a>
                ))}
              </nav>
            </div>

            {/* Sections */}
            <div className="space-y-10 sm:space-y-12">
              {t.sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-20">
                  <h2 className="font-['Barlow_Condensed'] font-bold text-xl sm:text-2xl text-white mb-4 sm:mb-5">{section.heading}</h2>
                  <RenderContent items={section.content} />
                </section>
              ))}
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-16">
        <div className="mx-auto max-w-[900px] px-5 sm:px-6 py-8 sm:py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-['Barlow_Condensed'] font-bold text-lg tracking-wider text-white mb-1">TEMPO</p>
            <p className="text-[10px] sm:text-xs text-white/20">&copy; {new Date().getFullYear()} Kviteberg AS. All rights reserved.</p>
          </div>
          <Link to={t.backLink} className="text-xs text-white/30 hover:text-white/50 transition-colors duration-180">{t.backLabel}</Link>
        </div>
      </footer>
    </div>
  );
}
